import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { connect } from 'react-redux';
import { X, Tag } from 'lucide-react';
import { getTags } from '../../actions/tagActions';
import SubmissionFormFormSkeleton from './views/FormView/SubmissionFormFormSkeleton';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'align',
  'blockquote', 'code-block',
  'link', 'image', 'video'
];

const SubmissionFormForm = ({ mode = 'new', submissionForm = null, onSubmit, onClose, tag: { tags }, getTags }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [linkRequired, setLinkRequired] = useState(true);
  const [commentRequired, setCommentRequired] = useState(true);
  const [achievementImageRequired, setAchievementImageRequired] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [automaticApproval, setAutomaticApproval] = useState(false);
  const [repeatable, setRepeatable] = useState(false);
  const [experiencePoints, setExperiencePoints] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const tagDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    getTags();
  }, [getTags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoading(true);
      try {
        if (submissionForm) {
          setTitle(submissionForm.title || '');
          setDescription(submissionForm.description || '');
          setLinkRequired(submissionForm.linkRequired !== false);
          setCommentRequired(submissionForm.commentRequired !== false);
          setAchievementImageRequired(submissionForm.achievementImageRequired || false);
          setCourseId(submissionForm.courseId || '');
          setAutomaticApproval(submissionForm.automaticApproval || false);
          setRepeatable(submissionForm.repeatable || false);
          setExperiencePoints(submissionForm.experiencePoints || 0);
          setSelectedTags(submissionForm.tags ? submissionForm.tags.map(tag => tag._id) : []);
        } else {
          setTitle('');
          setDescription('');
          setLinkRequired(true);
          setCommentRequired(true);
          setAchievementImageRequired(false);
          setCourseId('');
          setAutomaticApproval(false);
          setRepeatable(false);
          setExperiencePoints(0);
          setSelectedTags([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeForm();
  }, [submissionForm]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    const formData = {
      title: title.trim(),
      description: description.trim(),
      linkRequired,
      commentRequired,
      achievementImageRequired,
      courseId: courseId || null,
      automaticApproval,
      repeatable,
      experiencePoints,
      tags: selectedTags
    };
    
    if (onSubmit) onSubmit(formData);
    if (isNew) {
      setTitle('');
      setDescription('');
      setLinkRequired(true);
      setCommentRequired(true);
      setAchievementImageRequired(false);
      setCourseId('');
      setExperiencePoints(0);
      setSelectedTags([]);
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
    setTagSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeTag = (tagId) => {
    setSelectedTags(prev => prev.filter(id => id !== tagId));
  };

  const getSelectedTagObjects = () => {
    return tags.filter(tag => selectedTags.includes(tag._id));
  };

  const getFilteredTags = () => {
    return tags.filter(tag => 
      !selectedTags.includes(tag._id) && 
      tag.title.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  };

  const handleTagInputClick = () => {
    setShowTagDropdown(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Backspace' && tagSearchTerm === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-[#054581]">
          {isNew && 'Add New Submission Form'}
          {isEdit && 'Edit Submission Form'}
          {isView && 'View Submission Form'}
        </h3>
        <button
          onClick={onClose}
          type="button"
          className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {isLoading ? (
        <SubmissionFormFormSkeleton />
      ) : (
        <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-[#054581] mb-2">
                Form Title *
              </label>
              <input
                type="text"
                id="title"
                placeholder="Enter submission form title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                required
                disabled={isView}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-[#054581] mb-2">
                Description
              </label>
              <div className="border border-[#1086a4] rounded-md overflow-hidden" style={{ height: '200px' }}>
                <ReactQuill
                  value={description}
                  onChange={setDescription}
                  modules={quillModules}
                  formats={quillFormats}
                  className="bg-[#f9fafb] h-full flex flex-col"
                  readOnly={isView}
                  placeholder="Enter form description"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                />
                <style>
                  {`
                    .ql-container {
                      flex: 1;
                      overflow: auto;
                      height: auto !important;
                    }
                    .ql-toolbar {
                      position: sticky;
                      top: 0;
                      z-index: 1;
                      background: #f9fafb;
                    }
                  `}
                </style>
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={commentRequired}
                  onChange={e => setCommentRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">Comment Required</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={linkRequired}
                  onChange={e => setLinkRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">Link Required</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={achievementImageRequired}
                  onChange={e => setAchievementImageRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">Image Required</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={automaticApproval}
                  onChange={e => setAutomaticApproval(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">
                  Automatic Approval
                  <span className="ml-1 text-xs text-gray-500">
                    (Submissions will be automatically approved without manual review)
                  </span>
                </span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={repeatable}
                  onChange={e => setRepeatable(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">
                  Repeatable Submission Form
                  <span className="ml-1 text-xs text-gray-500">
                    (Allow users to submit multiple times)
                  </span>
                </span>
              </label>
            </div>

            <div>
              <label htmlFor="experiencePoints" className="block text-sm font-medium text-[#054581] mb-2">
                Experience Points
              </label>
              <input
                type="number"
                id="experiencePoints"
                min="0"
                max="1000"
                value={experiencePoints}
                onChange={e => setExperiencePoints(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                placeholder="0"
                disabled={isView}
              />
              <span className="text-xs text-gray-500 mt-1">
                Points awarded when mission is completed (default: 0)
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#054581] mb-2">
                Tags
              </label>
              {!isView && (
                <div className="relative" ref={tagDropdownRef}>
                  <div
                    onClick={handleTagInputClick}
                    className="min-h-[42px] px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md cursor-text focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:border-transparent transition-all"
                  >
                    <div className="flex flex-wrap items-center gap-1">
                      {getSelectedTagObjects().map(tag => (
                        <span
                          key={tag._id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-20 text-[#1086a4] border border-[#1086a4] border-opacity-30"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag.title}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTag(tag._id);
                            }}
                            className="ml-1 text-[#1086a4] hover:text-[#054581] hover:bg-[#1086a4] hover:bg-opacity-20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={tagSearchTerm}
                        onChange={(e) => setTagSearchTerm(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                        onFocus={() => setShowTagDropdown(true)}
                        placeholder={selectedTags.length === 0 ? "Search and select tags..." : "Search tags..."}
                        className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm"
                      />
                    </div>
                  </div>
                  {showTagDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#1086a4] rounded-md shadow-lg max-h-48 overflow-auto">
                      {getFilteredTags().length > 0 ? (
                        getFilteredTags().map(tag => (
                          <button
                            key={tag._id}
                            type="button"
                            onClick={() => toggleTag(tag._id)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center"
                          >
                            <Tag className="w-3 h-3 mr-2 text-[#1086a4]" />
                            {tag.title}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500 text-sm">
                          {tagSearchTerm ? `No tags found for "${tagSearchTerm}"` : 'No more tags available'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isView && getSelectedTagObjects().length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {getSelectedTagObjects().map(tag => (
                    <span
                      key={tag._id}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag.title}
                    </span>
                  ))}
                </div>
              )}
            </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
            >
              Cancel
            </button>
            {!isView && (
              <button
                type="submit"
                className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                {isNew ? 'Add Form' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      )}
    </form>
  );
};

const mapStateToProps = state => ({
  tag: state.tag
});

export default connect(mapStateToProps, { getTags })(SubmissionFormForm); 